import type { IDataCenter, IEnSaaSDataCenter } from '@/assets/types/api/EnSaaS';
import type {
  IPricingInfo,
  IPricingInfoAdditionalInfo,
  IPricingPlan,
  IPricingServiceType,
  IProductInfo,
  IProductPricing,
} from '@/assets/types/api/Products';
import { EnumServiceType } from '@/assets/types/api/Products';

enum EnumPricingLeadType {
  CONTACT_SALES = 'contact-sales',
  REQUEST_FOR_FREE_TRIAL = 'request-for-free-trial',
}

interface IPricePlan extends IPricingPlan {
  serviceType: EnumServiceType;
}

interface IPricePlanData {
  tabId: EnumServiceType;
  plans: IPricePlan[];
}

// 表單

interface IForm {
  message: string;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: number;
}

// 表單錯誤訊息管理

interface IFormErrorMessage {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: string;
  agreement: string;
}

const getForm = (): IForm => ({
  message: '',
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: 0,
});

const getFormErrorMessage = (): IFormErrorMessage => ({
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: '',
  agreement: '',
});

export const usePricingStore = defineStore('pricingStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const detail = ref<IProductInfo>({
    id: null,
    agreementId: null,
    banner: {
      name: '',
      description: '',
      webBanner: null,
      tabletBanner: null,
      mobileBanner: null,
      isFreeTrial: false,
    },
    announcement: null,
    overview: {
      sectionTitle: '',
      mediaType: 1,
      mediaWebThumbnail: '',
      mediaTabletThumbnail: '',
      mediaMobileThumbnail: '',
      videoUrl: null,
      features: [],
    },
    capabilities: {
      sectionTitle: '',
      items: [],
    },
    learnMore: {
      sectionTitle: '',
      recommendProducts: [],
    },
  });

  const pricingDetail = ref<IPricingInfo>({
    datasheetUrl: null,
    overview: '',
    additionalInfo: {
      minHardware: {
        operatingSystem: '',
        containerDeployment: '',
        hardware: '',
        displayResolution: '',
        usb: '',
        networkEnvironment: '',
      },
      protocols: {
        dataAcquisition: '',
        dataForwarding: '',
      },
      availableLanguages: [],
    },
  });

  const pricing = ref<IProductPricing>({
    serviceType: [],
  });
  const form = ref<IForm>(getForm());
  const formErrorMessage = ref<IFormErrorMessage>(getFormErrorMessage());

  const dataCenter = ref<{
    partNumber: string;
    serviceName: string;
    dataCenters: IDataCenter[];
    isShowPopUp: boolean;
  }>({
    partNumber: '',
    serviceName: '',
    dataCenters: [],
    isShowPopUp: false,
  });

  const isShowResult = ref<boolean>(false);
  const isSubmitting = ref<boolean>(false);
  const isShowMobileMenu = ref<boolean>(false);
  const isAgreementChecked = ref<boolean>(false);
  const agreementContent = ref<string>('');
  const isShowAgreementPopUp = ref<boolean>(false);

  /* Getters */

  const productName = computed((): string => {
    return detail.value.banner.name;
  });

  const pricingDatasheetUrl = computed((): string => {
    return pricingDetail.value.datasheetUrl || '';
  });

  const pricingOverview = computed((): string => {
    return pricingDetail.value.overview;
  });

  const pricingAdditionalInfo = computed((): IPricingInfoAdditionalInfo => {
    return pricingDetail.value.additionalInfo;
  });

  const serviceType = computed((): IPricingServiceType[] => {
    return pricing.value.serviceType;
  });

  const hasAgreement = computed((): boolean => {
    return detail.value.agreementId !== null;
  });

  const contextPricePlans = computed((): IPricePlanData[] => {
    /**
     * * NOTE: 把 LICENSE_UPGRADE 和 ON_PREMISES_LICENSE 兩種服務類型的方案合併在同一個 Tab 中
     * * 原因是後端目前是分開回傳這兩種服務類型的方案，但前端 UI 規劃上是要放在同一個 Tab 中顯示
     * * 接著把 serviceType id 加入到每個方案中，以便區分方案所屬的服務類型
     */

    const { LICENSE_UPGRADE, ON_PREMISES_LICENSE } = EnumServiceType;

    return pricing.value.serviceType.reduce((prev, current) => {
      const { id } = current;
      const plans = current.plans.map((plan) => ({
        ...plan,
        serviceType: id,
      }));

      if (id === LICENSE_UPGRADE || id === ON_PREMISES_LICENSE) {
        const index = prev.findIndex(
          ({ tabId }) => tabId === ON_PREMISES_LICENSE
        );
        if (index >= 0) {
          prev[index].plans = [...prev[index].plans, ...plans];
          return prev;
        }
        return [...prev, { tabId: ON_PREMISES_LICENSE, plans }];
      }
      return [...prev, { tabId: id, plans }];
    }, [] as IPricePlanData[]);
  });

  /* Actions */

  const USE_GET_PRODUCT_DETAIL = async () => {
    /**
     * 取得指定產品的詳細資訊
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    const id = computed((): string => {
      return !Array.isArray(route.params?.id) ? route.params.id || '' : '';
    });

    const partNumber = computed((): string => {
      return !Array.isArray(route.params?.partnumber)
        ? route.params.partnumber || ''
        : '';
    });

    return await useCustomFetch(
      () => {
        let url = `/marketplace/api/v1/products/${id.value}?websiteCode=${locale.value}`;
        if (partNumber.value) {
          url += `&partNumber=${partNumber.value}`;
        }
        return url;
      },
      {
        key: `USE_GET_PRICING_PRODUCT_DETAIL_${id.value}_${partNumber.value}`,
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            Object.assign(detail.value, _data);
          } else {
            console.error(_data);
          }
        },
      }
    );
  };

  const USE_GET_PRICING_DETAIL = async () => {
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    const id = computed((): string => {
      return !Array.isArray(route.params?.id) ? route.params.id || '' : '';
    });

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/products/pricing-details/${id.value}?websiteCode=${locale.value}`,
      {
        key: `USE_GET_PRICING_DETAIL_${id.value}`,
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            Object.assign(pricingDetail.value, _data);
          } else {
            console.error(_data);
          }
        },
      }
    );
  };
  const USE_GET_PRICING_PLANS = async () => {
    /**
     * 取得產品價格方案資訊（Client Only）
     */
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    const id = computed((): string => {
      return !Array.isArray(route.params?.id) ? route.params.id || '' : '';
    });

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/products/pricing-plans/${id.value}?websiteCode=${locale.value}`,
      {
        key: `USE_GET_PRICING_PLANS_${id.value}`,
        method: 'GET',
        lazy: true,
        immediate: true,
        server: false,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            Object.assign(pricing.value, _data);
          } else {
            console.error(_data);
          }
        },
      }
    );
  };

  const GET_DATA_CENTERS = async (): Promise<void> => {
    /**
     * 取得產品指定料號的資料中心
     */
    const { $customFetch, $notification } = nuxtApp;

    if (!dataCenter.value.partNumber) {
      return;
    }

    try {
      const data = await $customFetch<IEnSaaSDataCenter>(
        `/marketplace/api/v1/ensaas/data-centers/${dataCenter.value.partNumber}`,
        { method: 'GET' }
      );
      dataCenter.value.serviceName = data.serviceName;
      dataCenter.value.dataCenters = data.dataCenters;
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
    }
  };

  const RESET_DATA_CENTER = (): void => {
    dataCenter.value = {
      partNumber: '',
      serviceName: '',
      dataCenters: [],
      isShowPopUp: false,
    };
  };

  const POST_SUBSCRIBE_SERVICE = async (
    dataCenterCode: string
  ): Promise<void> => {
    /**
     * 取得產品料號指定的資料中心跳轉連結
     */
    const { $customFetch, $notification, $i18n } = nuxtApp;
    const { locale } = $i18n;

    const { partNumber, serviceName } = dataCenter.value;

    try {
      const data = await $customFetch<{ redirectUrl: string }>(
        `/marketplace/api/v1/ensaas/subscribe?websiteCode=${locale.value}`,
        {
          method: 'POST',
          body: { serverName: serviceName, dataCenterCode, partNumber },
        }
      );
      window.location.href = data.redirectUrl;
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
      RESET_DATA_CENTER();
      return Promise.reject(error);
    }
  };

  const POST_PRODUCTS_LEADS = async (
    leadTypeCode: EnumPricingLeadType,
    formData: FormData
  ): Promise<void> => {
    /**
     * 送出產品 Leads 收集相關表單
     */
    const { $customFetch, $notification, $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    try {
      await $customFetch(
        `/marketplace/api/v1/leads/products/${leadTypeCode}/${route.params.id}?websiteCode=${locale.value}`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
      return Promise.reject(error);
    }
  };

  const POST_PRICING_LEADS = async (
    leadTypeCode: EnumPricingLeadType,
    formData: FormData
  ): Promise<void> => {
    /**
     * 送出價格方案 Leads 收集相關表單
     */
    const { $customFetch, $notification, $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();

    try {
      await $customFetch(
        `/marketplace/api/v1/leads/pricing/${leadTypeCode}/${route.params.id}/${route.params.partnumber}?websiteCode=${locale.value}`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
      return Promise.reject(error);
    }
  };

  const GET_AGREEMENTS = async (): Promise<void> => {
    /**
     * 依編號及語系取得協議資料
     */
    const { $i18n, $notification, $customFetch } = nuxtApp;
    const { locale } = $i18n;
    const { agreementId } = detail.value;

    try {
      const data = await $customFetch<{ contentData: string }>(
        `/marketplace/api/v1/agreements/${agreementId}?websiteCode=${locale.value}`,
        { method: 'GET' }
      );
      agreementContent.value = data.contentData;
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
      return Promise.reject(error);
    }
  };

  const FILL_FORM_WITH_PROFILE = (): void => {
    /**
     * 用戶已登入時表單自動代入資料
     */

    const profileStore = useProfileStore();
    const { profile } = storeToRefs(profileStore);

    if (!profile.value.isLogin) return;

    form.value.firstName = profile.value.firstName ?? '';
    form.value.lastName = profile.value.lastName ?? '';
    form.value.email = profile.value.email ?? '';
    form.value.phoneCode = profile.value.mobileCountryCode ?? '';
    form.value.phoneNumber = profile.value.mobileNumber ?? '';
    form.value.companyName = profile.value.companyName ?? '';
    form.value.country = profile.value.country ?? '';
  };

  const VALID_FORM_FIRST_NAME = (): boolean => {
    /**
     * 驗證 firstName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.firstName === '') {
      formErrorMessage.value.firstName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.firstName = '';
    return true;
  };

  const VALID_FORM_LAST_NAME = (): boolean => {
    /**
     * 驗證 lastName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.lastName === '') {
      formErrorMessage.value.lastName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.lastName = '';
    return true;
  };

  const VALID_FORM_COMPANY_NAME = (): boolean => {
    /**
     * 驗證 companyName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.companyName === '') {
      formErrorMessage.value.companyName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.companyName = '';
    return true;
  };

  const VALID_FORM_EMAIL = (): boolean => {
    /**
     * 驗證 email 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.email === '') {
      formErrorMessage.value.email = t('form.error-required-message');
      return false;
    }

    if (!validateCompanyEmail(form.value.email)) {
      formErrorMessage.value.email = t('form.error-email-message');
      return false;
    }

    formErrorMessage.value.email = '';
    return true;
  };

  const VALID_FORM_PHONE_CODE = (): boolean => {
    /**
     * 驗證 phoneCode 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneCode === '') {
      formErrorMessage.value.phoneCode = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.phoneCode = '';
    return true;
  };

  const VALID_FORM_PHONE_NUMBER = (): boolean => {
    /**
     * 驗證 phoneNumber 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneNumber === '') {
      formErrorMessage.value.phoneNumber = t('form.error-required-message');
      return false;
    }

    const isNumeric = /^\d+$/.test(form.value.phoneNumber);
    if (!isNumeric) {
      formErrorMessage.value.phoneNumber = t('form.error-numeric-only');
      return false;
    }

    formErrorMessage.value.phoneNumber = '';
    return true;
  };

  const VALID_FORM_COUNTRY = (): boolean => {
    /**
     * 驗證 country 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.country === '') {
      formErrorMessage.value.country = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.country = '';
    return true;
  };

  const VALID_FORM_EMPLOYEES_RANGE = (): boolean => {
    /**
     * 驗證 employeeRange 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    const mainStore = useMainStore();
    const { employeeRanges } = storeToRefs(mainStore);

    if (form.value.employeeRange === 0) {
      formErrorMessage.value.employeeRange = t('form.error-required-message');
      return false;
    }

    const isValidRange = employeeRanges.value.some(
      (range) => range.id === form.value.employeeRange
    );

    if (!isValidRange) {
      formErrorMessage.value.employeeRange = t('form.error-invalid-selection');
      return false;
    }

    formErrorMessage.value.employeeRange = '';
    return true;
  };

  const VALID_FORM_AGREEMENT = (): boolean => {
    /**
     * 驗證協議 Checkbox 欄位（僅在方案有設定協議時驗證）
     * 並回傳驗證狀態（驗證通過回傳 True）
     */

    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (!hasAgreement.value) return true;

    if (!isAgreementChecked.value) {
      formErrorMessage.value.agreement = t('form.error-agreement');
      return false;
    }

    formErrorMessage.value.agreement = '';
    return true;
  };

  const VALID_FORM = (): boolean => {
    /**
     * 驗證所有欄位（驗證通過回傳 True）
     */

    const result = [
      VALID_FORM_FIRST_NAME(),
      VALID_FORM_LAST_NAME(),
      VALID_FORM_COMPANY_NAME(),
      VALID_FORM_EMAIL(),
      VALID_FORM_PHONE_CODE(),
      VALID_FORM_PHONE_NUMBER(),
      VALID_FORM_COUNTRY(),
      VALID_FORM_EMPLOYEES_RANGE(),
      VALID_FORM_AGREEMENT(),
    ];

    return result.every((value) => value === true);
  };

  const RESET_FORM = (): void => {
    /**
     * 重置表單
     */

    form.value = getForm();
    formErrorMessage.value = getFormErrorMessage();
    isAgreementChecked.value = false;
  };

  return {
    detail,
    pricingDetail,
    pricing,
    form,
    formErrorMessage,
    isShowResult,
    isSubmitting,
    isShowMobileMenu,
    isAgreementChecked,
    agreementContent,
    isShowAgreementPopUp,
    dataCenter,
    productName,
    serviceType,
    hasAgreement,
    contextPricePlans,
    pricingDatasheetUrl,
    pricingOverview,
    pricingAdditionalInfo,
    USE_GET_PRODUCT_DETAIL,
    USE_GET_PRICING_DETAIL,
    USE_GET_PRICING_PLANS,
    GET_DATA_CENTERS,
    GET_AGREEMENTS,
    POST_SUBSCRIBE_SERVICE,
    POST_PRODUCTS_LEADS,
    POST_PRICING_LEADS,
    FILL_FORM_WITH_PROFILE,
    VALID_FORM_FIRST_NAME,
    VALID_FORM_LAST_NAME,
    VALID_FORM_COMPANY_NAME,
    VALID_FORM_EMAIL,
    VALID_FORM_PHONE_CODE,
    VALID_FORM_PHONE_NUMBER,
    VALID_FORM_COUNTRY,
    VALID_FORM_EMPLOYEES_RANGE,
    VALID_FORM_AGREEMENT,
    VALID_FORM,
    RESET_FORM,
    RESET_DATA_CENTER,
  };
});

export { EnumPricingLeadType };
export type { IPricePlan, IPricePlanData };
